import { Profile } from '@bioliberty/lifemind';
import { Info } from 'lucide-react';
import { useCallback, useState } from 'react';
import z from 'zod';
import { useUpdateUserMutation } from '../../api';
import { Button } from '../../components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { TextInput } from '../../components/Input/TextInput';
import { Switch } from '../../components/Switch';
import { useToaster } from '../../hooks/useToaster';
import { useValidator } from '../../hooks/useValidator';

interface EmailSettingsModalProps {
  user: Profile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EmailSettingsModal = ({ user, open, onOpenChange }: EmailSettingsModalProps) => {
  const [newEmail, setNewEmail] = useState('');
  const [newEmailConfirmation, setNewEmailConfirmation] = useState('');
  const [sessionEmailOptIn, setSessionEmailOptIn] = useState(user.sessionEmailOptIn);
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const toaster = useToaster();

  const { validate, errors } = useValidator(
    z
      .object({
        newEmail: z.string().optional(),
        newEmailConfirmation: z.string().optional(),
        sessionEmailOptIn: z.boolean()
      })
      .refine(({ newEmail }) => !newEmail || z.email().safeParse(newEmail).success, {
        message: 'Must be a valid email',
        path: ['newEmail']
      })
      .refine(({ newEmail }) => !newEmail || newEmail !== user.email, {
        message: 'The new email must be different to your current email',
        path: ['newEmail']
      })
      .refine(({ newEmail, newEmailConfirmation }) => !newEmail || newEmail === newEmailConfirmation, {
        message: 'The email confirmation must match',
        path: ['newEmailConfirmation']
      })
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        setNewEmail('');
        setNewEmailConfirmation('');
        setSessionEmailOptIn(user.sessionEmailOptIn);
      }
    },
    [onOpenChange, user.sessionEmailOptIn]
  );

  const onUpdate = useCallback(async () => {
    if (!validate({ newEmail, newEmailConfirmation, sessionEmailOptIn })) {
      return;
    }

    try {
      await updateUser({
        id: user.id,
        ...(newEmail ? { email: newEmail } : {}),
        firstName: user.givenName || user.name.split(' ')[0],
        lastName: user.familyName || user.name.split(' ')[1],
        roles: user.roles,
        sessionEmailOptIn
      }).unwrap();

      setNewEmail('');
      setNewEmailConfirmation('');
      onOpenChange(false);

      toaster.success(
        `Email preferences updated.${newEmail ? ' Please check your new email address for a verification link.' : ''}`
      );
    } catch {
      toaster.error('Failed to update. Please try again.');
    }
  }, [
    newEmail,
    newEmailConfirmation,
    sessionEmailOptIn,
    onOpenChange,
    toaster,
    updateUser,
    user.familyName,
    user.givenName,
    user.id,
    user.name,
    user.roles,
    validate
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Email Settings</DialogTitle>
          <DialogDescription>
            An email with a verification link will be sent to the new email address.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-2">
            <TextInput
              label="New Email"
              onChange={(value) => setNewEmail(value)}
              errorMessage={errors.newEmail}
              autoFocus
            />

            <TextInput
              label="Confirm New Email"
              onChange={(value) => setNewEmailConfirmation(value)}
              errorMessage={errors.newEmailConfirmation}
            />
          </div>

          <>
            <div className="mt-4 text-lg font-semibold">Email Preferences</div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-4">
                <div className="text-sm">
                  Automatically receive an AI-generated session summary after each patient session.
                </div>

                <div className="flex flex-row items-center gap-2 text-sm">
                  <Switch checked={sessionEmailOptIn} onChange={setSessionEmailOptIn} />
                  <div>Session summary emails</div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Icon type={Info} />
                <span>
                  {sessionEmailOptIn
                    ? "We'll send you a session summary once you've been inactive for 15 minutes."
                    : "You won't receive session summaries by email."}
                </span>
              </div>
            </div>
          </>
        </div>

        <DialogFooter>
          <Button
            variant="accent"
            onClick={onUpdate}
            isLoading={isLoading}
            disabled={
              isLoading ||
              (!newEmail && sessionEmailOptIn === user.sessionEmailOptIn) ||
              (!!newEmail && !newEmailConfirmation)
            }
          >
            Update
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
